/* Fuego & Fragancia — Header: mega-menú, panel móvil y sticky */

if (!customElements.get('ff-header')) {
  customElements.define(
    'ff-header',
    class FfHeader extends HTMLElement {
      connectedCallback() {
        this.toggle = this.querySelector('[data-ff-menu-toggle]');
        this.panel = this.querySelector('[data-ff-menu-panel]');
        this.overlay = this.querySelector('[data-ff-menu-overlay]');

        this.initDrawer();
        this.initMegaMenu();
        this.initTabs();
        this.initCarousels();

        if (this.dataset.sticky === 'true') {
          this.initSticky();
        }
      }

      /* ---------- Panel del botón de menú ---------- */

      initDrawer() {
        if (!this.toggle || !this.panel) return;

        this.toggle.addEventListener('click', () => this.toggleMenu());

        if (this.overlay) {
          this.overlay.addEventListener('click', () => this.close());
        }

        document.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          if (this.isOpen()) this.close();
          this.closeMega();
        });

        this.panel.addEventListener('click', (event) => {
          if (event.target.closest('a')) this.close();
        });
      }

      isOpen() {
        return this.hasAttribute('open-menu');
      }

      toggleMenu() {
        this.isOpen() ? this.close() : this.open();
      }

      open() {
        this.setAttribute('open-menu', '');
        this.toggle.setAttribute('aria-expanded', 'true');
        if (this.wrapper) this.wrapper.classList.remove('ff-sticky-hidden');
      }

      close() {
        this.removeAttribute('open-menu');
        this.toggle.setAttribute('aria-expanded', 'false');
      }

      /* ---------- Mega-menú (escritorio, al pasar el cursor) ---------- */

      initMegaMenu() {
        this.megaLayer = this.querySelector('[data-ff-mega-layer]');
        this.triggers = Array.from(this.querySelectorAll('[data-mega-trigger]'));
        if (!this.megaLayer || !this.triggers.length) return;

        // Pequeño retardo al salir: evita que el panel parpadee mientras el
        // cursor viaja del enlace al panel, que están separados por un hueco.
        this.megaCloseDelay = 160;

        this.triggers.forEach((trigger) => {
          const id = trigger.dataset.megaTrigger;
          const panel = this.megaLayer.querySelector(`[data-mega-panel="${id}"]`);
          if (!panel) return;

          const openThis = () => {
            clearTimeout(this.megaTimer);
            this.openMega(trigger, panel);
          };
          const closeSoon = () => {
            clearTimeout(this.megaTimer);
            this.megaTimer = setTimeout(() => this.closeMega(), this.megaCloseDelay);
          };

          trigger.addEventListener('mouseenter', openThis);
          trigger.addEventListener('mouseleave', closeSoon);
          panel.addEventListener('mouseenter', () => clearTimeout(this.megaTimer));
          panel.addEventListener('mouseleave', closeSoon);

          // Accesible por teclado: al enfocar el enlace se abre.
          const link = trigger.querySelector('a');
          if (link) link.addEventListener('focus', openThis);
        });

        // Si el cursor sale del header por completo, se cierra.
        this.addEventListener('mouseleave', () => {
          clearTimeout(this.megaTimer);
          this.megaTimer = setTimeout(() => this.closeMega(), this.megaCloseDelay);
        });
      }

      openMega(trigger, panel) {
        this.megaLayer.querySelectorAll('.ff-mega').forEach((el) => {
          if (el !== panel) el.classList.remove('is-open');
        });
        this.triggers.forEach((item) => {
          const link = item.querySelector('a');
          if (link) link.setAttribute('aria-expanded', item === trigger ? 'true' : 'false');
        });

        panel.classList.add('is-open');
        panel.setAttribute('aria-hidden', 'false');
        trigger.classList.add('is-active');
      }

      closeMega() {
        if (!this.megaLayer) return;
        this.megaLayer.querySelectorAll('.ff-mega').forEach((el) => {
          el.classList.remove('is-open');
          el.setAttribute('aria-hidden', 'true');
        });
        this.triggers.forEach((item) => {
          item.classList.remove('is-active');
          const link = item.querySelector('a');
          if (link) link.setAttribute('aria-expanded', 'false');
        });
      }

      /* ---------- Pestañas (sirven igual en escritorio y en el cajón) ---------- */

      initTabs() {
        // Mismo comportamiento para el mega-menú de escritorio y el cajón.
        this.querySelectorAll('.ff-mega, .ff-drawer-menu').forEach((mega) => {
          const tabs = Array.from(mega.querySelectorAll('.ff-mega__tab'));
          const groups = Array.from(mega.querySelectorAll('.ff-mega__group'));
          if (!tabs.length) return;

          tabs.forEach((tab) => {
            tab.addEventListener('click', (event) => {
              event.preventDefault();
              const target = tab.dataset.tab;

              tabs.forEach((t) => {
                const active = t === tab;
                t.classList.toggle('is-active', active);
                t.setAttribute('aria-selected', active);
              });

              groups.forEach((group) => {
                group.classList.toggle('is-active', group.dataset.group === target);
              });
            });
          });
        });
      }

      /* ---------- Carrusel del mega-menú (solo escritorio) ----------
         Tres formas de recorrerlo: la barra, la rueda del ratón y arrastrando.
         En el cajón móvil los productos van en columna, así que no aplica. */

      initCarousels() {
        const tracks = this.querySelectorAll('.ff-header__mega-layer .ff-mega__track');
        if (!tracks.length) return;

        tracks.forEach((track) => {
          this.markScrollable(track);

          // Al abrir el panel o cambiar de pestaña, el ancho cambia: re-medir.
          if ('ResizeObserver' in window) {
            new ResizeObserver(() => this.markScrollable(track)).observe(track);
          }

          this.enableWheelScroll(track);
          this.enableDragScroll(track);
        });
      }

      // Con pocos productos van centrados; con overflow hay que alinearlos al
      // inicio o el primero queda inalcanzable al desplazar.
      markScrollable(track) {
        const scrollable = track.scrollWidth > track.clientWidth + 1;
        track.classList.toggle('is-scrollable', scrollable);
      }

      enableWheelScroll(track) {
        track.addEventListener(
          'wheel',
          (event) => {
            if (!track.classList.contains('is-scrollable')) return;
            // Un trackpad que ya desplaza en horizontal se deja en paz.
            if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

            const atStart = track.scrollLeft <= 0;
            const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
            // En los extremos se devuelve el gesto a la página, para no
            // secuestrar el scroll vertical.
            if ((atStart && event.deltaY < 0) || (atEnd && event.deltaY > 0)) return;

            event.preventDefault();
            track.scrollLeft += event.deltaY;
          },
          { passive: false }
        );
      }

      enableDragScroll(track) {
        let dragging = false;
        let captured = false;
        let startX = 0;
        let startScroll = 0;
        let moved = false;

        track.addEventListener('pointerdown', (event) => {
          // El táctil ya arrastra de forma nativa; esto es solo para el ratón.
          if (event.pointerType !== 'mouse') return;
          if (!track.classList.contains('is-scrollable')) return;

          dragging = true;
          moved = false;
          captured = false;
          startX = event.clientX;
          startScroll = track.scrollLeft;
        });

        track.addEventListener('pointermove', (event) => {
          if (!dragging) return;
          const delta = event.clientX - startX;

          if (!moved && Math.abs(delta) > 4) {
            moved = true;
            track.classList.add('is-dragging');
            // La captura se pide solo al confirmar un arrastre real: pedirla
            // ya en pointerdown reencamina también el "click" de un simple
            // clic hacia el track, y el <a> del producto deja de navegar.
            track.setPointerCapture(event.pointerId);
            captured = true;
          }

          if (moved) track.scrollLeft = startScroll - delta;
        });

        const stop = (event) => {
          if (!dragging) return;
          dragging = false;
          track.classList.remove('is-dragging');
          if (captured && event.pointerId != null && track.hasPointerCapture(event.pointerId)) {
            track.releasePointerCapture(event.pointerId);
          }
          captured = false;
        };

        track.addEventListener('pointerup', stop);
        track.addEventListener('pointercancel', stop);

        // Si se arrastró, el soltar no debe abrir el producto.
        track.addEventListener(
          'click',
          (event) => {
            if (!moved) return;
            event.preventDefault();
            event.stopPropagation();
            moved = false;
          },
          true
        );
      }

      /* ---------- Sticky ---------- */

      initSticky() {
        this.wrapper = this.closest('.shopify-section') || this;
        this.lastScroll = window.scrollY;
        this.threshold = 6;

        const onScroll = () => {
          const current = window.scrollY;
          const delta = current - this.lastScroll;

          this.classList.toggle('is-scrolled', current > 24);

          if (Math.abs(delta) < this.threshold) return;

          if (current <= 0 || this.isOpen()) {
            this.wrapper.classList.remove('ff-sticky-hidden');
          } else if (delta > 0 && current > this.offsetHeight) {
            this.wrapper.classList.add('ff-sticky-hidden');
            this.closeMega();
          } else if (delta < 0) {
            this.wrapper.classList.remove('ff-sticky-hidden');
          }

          this.lastScroll = current;
        };

        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
      }
    }
  );
}
